import React, { useCallback, useEffect, useState } from 'react';
import type KeenSlider from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';
import type { ChildrenProp, ClassNameProp, StateSetter } from '../common-types';
import { Tool } from '../basic';
import type { TOptionsEvents } from 'keen-slider';
import { isEmptyString } from '../../util';
import { intl } from '../../lib';

/**
 * Props for a {@link Carousel} component.
 */
export interface CarouselProps
  extends Required<ChildrenProp>,
    Omit<TOptionsEvents, 'slideChanged'>,
    ClassNameProp {
  /**
   * If the left/right arrows should be shown or not.
   */
  arrows?: boolean;
  /**
   *  Custom text - tooltip and aria-label - for the arrows.
   *  Mainly make sense for the case when there are two options or the text is generic.
   *  By default, it is "Next" and "Previous"
   */
  arrowsTooltip?: { previous: string; next: string };
  /**
   * If the dots should be shown or not.
   *
   * You can also define the position of the dots here, if the default is not ideal. Make sure, that
   * the dots are not overlapping with the arrows, which are always in the same positions.
   */
  dots?: boolean | 'left' | 'right' | 'top' | 'bottom';
  /**
   * If the position of the slider needs to be manipulated from the outside.
   * This is needed especially for animations etc. to work as intended.
   */
  controlledState?: {
    /**
     * Current value of the state.
     */
    value: number;
    /**
     * Setter for the state.
     */
    setValue: StateSetter<number>;
  };
  /**
   * If the events should be cached. This is to increase performance for
   * when none of the event callbacks need any kind of state values.
   */
  cacheEvents?: boolean;
}

/**
 * A generic Carousel component which can be used for Card-like layouts, image carousels, etc.
 * This component uses [`keen-slider`](https://keen-slider.io) internally.
 *
 * Important to note is that this component is prone to breaking when its visibility is set to `display:none`.
 * Therefore, in situations where this component is meant to be hidden, make sure to not render it entirely.
 *
 * @param properties a {@link CarouselProps} object.
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function Carousel({
  children,
  duration = 500,
  controls = true,
  initial = 0,
  arrows = true,
  dots = true,
  loop = false,
  vertical = false,
  className = '',
  controlledState,
  cacheEvents = false,
  afterChange,
  beforeChange,
  dragEnd,
  dragStart,
  ...props
}: Readonly<CarouselProps>): JSX.Element {
  // internal state managed by the slider
  const [currentSlide, setCurrentSlide] = useState(0);

  // internal states for the events, to trigger them with the correct data
  // this is a workaround, because the keen-slider doesn't update events properly when their dependencies change
  // and fixing that in the keen-slider internally is easier said than done
  type EventStateWorkaround = [KeenSlider, number] | null;
  const [
    afterChangeState,
    setAfterChangeState
  ] = useState<EventStateWorkaround>(null);
  const [
    beforeChangeState,
    setBeforeChangeState
  ] = useState<EventStateWorkaround>(null);
  const [dragEndState, setDragEndState] = useState<EventStateWorkaround>(null);
  const [dragStartState, setDragStartState] = useState<EventStateWorkaround>(
    null
  );

  // helper method to create a callback for the event states
  const createEventStateCallback = useCallback(
    (
      instance: KeenSlider,
      stateSetter: typeof setAfterChangeState,
      regularCallback: typeof afterChange
    ) => {
      if (cacheEvents) {
        regularCallback?.(instance);
      } else {
        const newSlide = instance.details().relativeSlide;
        stateSetter([instance, newSlide]);
      }
    },
    [cacheEvents]
  );

  // slider definition
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    ...props,
    initial,
    controls,
    duration,
    loop,
    vertical,
    slideChanged: instance => {
      const newSlide = instance.details().relativeSlide;
      setCurrentSlide(newSlide);
    },
    afterChange: instance => {
      if (controlledState) {
        // make sure the outside state is updated when the slide changed
        // this happens only after the animation finishes
        const newSlide = instance.details().relativeSlide;
        controlledState.setValue(newSlide);
      }
      // trigger callback
      if (afterChange && !cacheEvents) {
        setAfterChangeState([instance, instance.details().relativeSlide]);
      } else if (afterChange) {
        afterChange(instance);
      }
    },
    beforeChange: beforeChange
      ? instance =>
          createEventStateCallback(instance, setBeforeChangeState, beforeChange)
      : undefined,
    dragEnd: dragEnd
      ? instance => createEventStateCallback(instance, setDragEndState, dragEnd)
      : undefined,
    dragStart: dragStart
      ? instance =>
          createEventStateCallback(instance, setDragStartState, dragStart)
      : undefined
  });

  // manipulate the slider if the controlledState changed
  useEffect(() => {
    if (
      slider &&
      controlledState &&
      slider.details().relativeSlide !== controlledState.value
    ) {
      slider.moveToSlideRelative(controlledState.value);
    }
  }, [slider, controlledState]);

  // useEffect for all the 'change' events
  // since the slider caches the events in a way that is not easily fixed, which leads to
  // the events using old state data which might be incorrect for the component using the Carousel.
  useEffect(() => {
    if (afterChangeState) {
      afterChange?.(afterChangeState[0]);
      setAfterChangeState(null);
    }
  }, [afterChangeState, afterChange]);
  useEffect(() => {
    if (beforeChangeState) {
      beforeChange?.(beforeChangeState[0]);
      setBeforeChangeState(null);
    }
  }, [beforeChange, beforeChangeState]);
  useEffect(() => {
    if (dragEndState) {
      dragEnd?.(dragEndState[0]);
      setDragEndState(null);
    }
  }, [dragEnd, dragEndState]);
  useEffect(() => {
    if (dragStartState) {
      dragStart?.(dragStartState[0]);
      setDragStartState(null);
    }
  }, [dragStart, dragStartState]);

  // container classes
  const elementClasses = ['ehs-carousel'];
  if (vertical) elementClasses.push('vertical');
  if (!isEmptyString(className)) elementClasses.push(className);

  // determine location of the 'dots', if they are to be displayed
  let dotsLocation;
  if (typeof dots === 'boolean' && !vertical) {
    dotsLocation = 'bottom';
  } else if (typeof dots === 'boolean' && vertical) {
    dotsLocation = 'right';
  } else {
    dotsLocation = dots;
  }

  return (
    <div {...props} className={elementClasses.join(' ')}>
      <div ref={sliderRef} className="keen-slider">
        {React.Children.map(children, child => (
          <div className="keen-slider__slide">{child}</div>
        ))}
      </div>
      {slider && arrows && (
        <>
          {(loop || currentSlide !== 0) && (
            <Tool
              className={`carousel-tool ${
                vertical ? 'carousel-up-arrow' : 'carousel-left-arrow'
              }`}
              icon={vertical ? 'arrow-up' : 'arrow-left'}
              tooltip={
                props.arrowsTooltip?.previous ?? intl('view.button.previous')
              }
              onClick={slider.prev}
            />
          )}
          {(loop || currentSlide !== slider.details().size - 1) && (
            <Tool
              className={`carousel-tool ${
                vertical ? 'carousel-down-arrow' : 'carousel-right-arrow'
              }`}
              icon={vertical ? 'arrow-down' : 'arrow-right'}
              tooltip={props.arrowsTooltip?.next ?? intl('view.button.next')}
              onClick={slider.next}
            />
          )}
        </>
      )}
      {slider && dots && (
        <div className={`dots ${dotsLocation}`}>
          {[...Array(slider.details().size).keys()].map(index => {
            return (
              // not using Button, because this is a very special case
              <button
                type="button"
                key={index}
                onClick={() => {
                  slider.moveToSlideRelative(index);
                }}
                className={'dot' + (currentSlide === index ? ' active' : '')}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
